/**
 * QrScannerDialog — opens the device camera and scans a QR code to
 * extract a 6-digit clinic code.
 *
 * Session 18 feature. Used by the "enter a clinic" page so the customer
 * can scan the clinic's QR instead of typing the code.
 *
 * Decoding strategy (progressive enhancement):
 *   1. Native `BarcodeDetector` if the browser supports the 'qr_code'
 *      format (Chrome on Android — our primary mobile target). Fast and
 *      battery-friendly.
 *   2. Fallback to the `jsqr` library, decoding frames drawn to an
 *      offscreen canvas. Works on browsers without BarcodeDetector
 *      (iOS Safari, Firefox).
 * Both run on the same requestAnimationFrame loop over a single hidden
 * <video> element fed by getUserMedia (rear camera preferred).
 *
 * What we extract: a clinic code is exactly 6 digits. A QR may encode
 * the bare code ("123456"), a URL (".../clinic/123456"), or a prefixed
 * token ("clinic:123456"). We normalize Persian/Arabic digits, then pick
 * the first standalone 6-digit run — so a longer order number won't be
 * mistaken for a code. On a hit we stop the camera and call
 * `onDetect(code)`; the parent owns what happens next (fill the field,
 * resolve the clinic).
 *
 * Camera access requires a secure context (HTTPS, or localhost in dev).
 * Permission denial / no camera / insecure origin all surface as a clear
 * inline error with a "type it instead" hint — manual entry always works.
 *
 * Lifecycle: the stream + rAF loop start when `open` flips true and are
 * fully torn down on close/unmount (all tracks stopped) so the camera
 * indicator doesn't linger.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(code: string) => void} onDetect  called once with a 6-digit code
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import jsQR from 'jsqr';

import { normalizeDigits } from '@/utils/digits';

/**
 * Pull a standalone 6-digit clinic code out of decoded QR text.
 * @param {string} text raw decoded QR payload
 * @returns {string|null} the 6-digit code, or null if none found
 */
function extractClinicCode(text) {
  const normalized = normalizeDigits(String(text || ''));
  const runs = normalized.match(/\d+/g) || [];
  return runs.find((r) => r.length === 6) || null;
}

export function QrScannerDialog({ open, onClose, onDetect }) {
  const { t } = useTranslation();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectedRef = useRef(false); // guard so we fire onDetect only once

  const [status, setStatus] = useState('starting'); // 'starting' | 'scanning' | 'error'
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    detectedRef.current = false;
    setStatus('starting');
    setErrorKey(null);

    let cancelled = false;
    let barcodeDetector = null;

    // Try to construct a native BarcodeDetector for QR codes.
    try {
      if ('BarcodeDetector' in window) {
        barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
      }
    } catch {
      barcodeDetector = null; // fall back to jsQR below
    }

    /** Stop the camera + scan loop. Safe to call multiple times. */
    const stop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };

    /** Handle a successful decode exactly once. */
    const handleResult = (rawText) => {
      if (detectedRef.current) return;
      const code = extractClinicCode(rawText);
      if (!code) return; // a QR that isn't a clinic code — keep scanning
      detectedRef.current = true;
      stop();
      onDetect(code);
    };

    const scan = async () => {
      if (cancelled || detectedRef.current) return;
      const video = videoRef.current;
      if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
        try {
          if (barcodeDetector) {
            const codes = await barcodeDetector.detect(video);
            if (codes && codes.length > 0) {
              handleResult(codes[0].rawValue);
            }
          } else {
            const canvas = canvasRef.current;
            if (canvas) {
              const w = video.videoWidth;
              const h = video.videoHeight;
              if (w && h) {
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(video, 0, 0, w, h);
                const imageData = ctx.getImageData(0, 0, w, h);
                const result = jsQR(imageData.data, w, h, {
                  inversionAttempts: 'dontInvert',
                });
                if (result?.data) handleResult(result.data);
              }
            }
          }
        } catch {
          // A transient decode error on one frame — keep looping.
        }
      }
      if (!cancelled && !detectedRef.current) {
        rafRef.current = requestAnimationFrame(scan);
      }
    };

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) {
          setStatus('error');
          setErrorKey('unsupported');
        }
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.setAttribute('playsinline', 'true'); // iOS: inline, not fullscreen
          video.srcObject = stream;
          await video.play();
        }
        setStatus('scanning');
        rafRef.current = requestAnimationFrame(scan);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
          setErrorKey('denied');
        } else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
          setErrorKey('noCamera');
        } else {
          setErrorKey('generic');
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      stop();
    };
    // onDetect is stable from the caller; we intentionally restart only on `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        {t('clinicEntry.scanner.title')}
        <IconButton
          onClick={onClose}
          aria-label={t('common.cancel')}
          size="small"
          sx={{ position: 'absolute', top: 12, insetInlineEnd: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {status === 'error' ? (
          <Box sx={{ py: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(`clinicEntry.scanner.error.${errorKey || 'generic'}`)}
            </Alert>
            <Button variant="contained" fullWidth onClick={onClose}>
              {t('clinicEntry.scanner.typeInstead')}
            </Button>
          </Box>
        ) : (
          <Box>
            {/* Square camera preview with a framing overlay */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'common.black',
              }}
            >
              <Box
                component="video"
                ref={videoRef}
                muted
                playsInline
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />

              {/* Targeting frame */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: '18%',
                  border: '3px solid',
                  borderColor: 'primary.light',
                  borderRadius: 2,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                }}
              />

              {status === 'starting' && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress color="inherit" sx={{ color: 'common.white' }} />
                </Box>
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 2 }}
            >
              {t('clinicEntry.scanner.hint')}
            </Typography>
          </Box>
        )}

        {/* Offscreen canvas used by the jsQR fallback path. */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>
    </Dialog>
  );
}

export default QrScannerDialog;
